import './footer.scss';

import {
    Button,
    Input,
    LocalizedLink,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import React, { useCallback, useContext, useState } from 'react';

import EnglanderLogo from '../../images/EnglanderVector.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SiteLocaleSelectionPopupMenu from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/SiteLocale/SiteLocaleSelectionPopupMenu';
import enveloppe from '../../images/englander-icons/Email-Icon.svg';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import facebook from '../../images/englander-icons/Facebook-Icon.svg';
import { feedbackContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/feedback/feedbackContext';
import progiLogo from '@progymedia/sbi-web-englander/src/images/Progy.svg';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useSubscribeNewsletterAction } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/customerAccount';
import { useTranslation } from 'react-i18next';
import youtube from '../../images/englander-icons/Youtube-Icon.svg';
import { useBoutikService } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/boutikService/useBoutikService';
import { useLoginContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/login/useLoginContext';
import { graphql, useStaticQuery } from 'gatsby';

export default function Footer(props: {
    venting: Map<any, any>;
    accessories: Map<any, any>;
    footerCms: any;
}) {
    const data = useStaticQuery(graphql`
        query {
            customerServiceInfo: allProductsJson(
                filter: { code: { eq: "englander_customer-service" } }
            ) {
                nodes {
                    ...contactUsInformationFragment
                }
            }
            technicalSupportInfo: allProductsJson(
                filter: { code: { eq: "englander_technical-support" } }
            ) {
                nodes {
                    ...contactUsInformationFragment
                }
            }
            retailerServiceInfo: allProductsJson(
                filter: { code: { eq: "englander_retailer-service" } }
            ) {
                nodes {
                    ...contactUsInformationFragment
                }
            }
        }
    `);

    const { t } = useTranslation('layout');
    const { venting, accessories, footerCms } = props;
    const { navigateTranslated } = usePageLocalization();
    const { isAuthenticated } = useBoutikService();
    const { setQuickLoginVisible } = useLoginContext();

    return (
        <footer>
            <InfoLetter footerCms={footerCms} />
            <FooterContent />
        </footer>
    );

    function InfoLetter(props: { footerCms: any }) {
        const { footerCms } = props;
        const [email, setEmail] = useState('');
        const { addAlert } = useContext(feedbackContext);
        const [subscribeNewsletter, subscribeNewsletterResponse] =
            useSubscribeNewsletterAction();
        const { currentLocale } = usePageLocalization();
        const subscribeEmail = useCallback(async () => {
            await subscribeNewsletter({ email })
                .then(() =>
                    addAlert({
                        message: t(
                            'toastMessages:subscribe-newsletter-success',
                            { email: email }
                        ),
                        status: 'success',
                    })
                )
                .catch((error) =>
                    addAlert({ message: error, status: 'error' })
                );
        }, [addAlert, email, subscribeNewsletter]);
        
        return (
            <div className='footer-infomail'>
                <img className='enveloppe' src={enveloppe} alt=''></img>
                <div className='enveloppe-text'>
                    {footerCms?.attributes?.texte_3?.values[0][currentLocale]}
                </div>

                <div className='email-input'>
                    <Input
                        value={email}
                        placeholder={t('footer.newsletter.placeholder')}
                        onChange={(value) => setEmail(value)}
                        onKeyDown={(key) => {
                            if (
                                key === 'Enter' &&
                                !subscribeNewsletterResponse.loading
                            )
                                subscribeEmail();
                        }}
                    />
                    <Button
                        className='newsletter-button'
                        onClick={() => {
                            if (!subscribeNewsletterResponse.loading)
                                subscribeEmail();
                        }}
                    >
                        <>
                            <span className='text'>
                                {t('footer.newsletter.submit')}
                            </span>
                            <FontAwesomeIcon
                                className='icon'
                                icon={faArrowRight}
                            />
                        </>
                    </Button>
                </div>
            </div>
        );
    }

    function FooterContent() {
        const { currentLocale } = usePageLocalization();
        const ventingMenuElem = Array.from(venting)[0][1]
            .sort((a: any, b: any) => a.label > b.label)
            .map((single: { label: string; category: string }) => {
                const category = single.category
                    .split('/')[3]
                    .toLowerCase()
                    .replaceAll(' ', '-');
                return {
                    title: single.label,
                    link: `/venting/${category}/`,
                };
            });
        const accessoriesMenuElem = Array.from(accessories)[0][1]
            .sort((a: any, b: any) => a.label > b.label)
            .map((single: { label: string; category: string }) => {
                return {
                    title: single.label,
                    link: (single as any)?.slug,
                };
            });

        const handleMyAccountClick = () => {
            if (isAuthenticated) {
                navigateTranslated('/account/my-account/');
            } else {
                setQuickLoginVisible(true);
            }
        };

        return (
            <div className='footer-content-top'>
                <div className='footer-content'>
                    <div className='footer-pages-list'>
                        <div className='footer-row'>
                            <div className='title'>
                                <MenuTitle title='appliances' />
                            </div>
                            <div className='links'>
                                <ul>
                                    <li>
                                        <MenuTitle
                                            title='camp-stoves'
                                            link='/heaters/camp-stoves/'
                                        />
                                    </li>
                                    <li>
                                        <MenuTitle
                                            title='wood-stoves'
                                            link='/heaters/wood-stoves/'
                                        />
                                    </li>                                    
                                    <li>
                                        <MenuTitle
                                            title='outdoor-fireplaces'
                                            link='/heaters/outdoor-fireplaces/'
                                        />
                                    </li>
                                </ul>
                            </div>
                            <div className='title venting'>
                                <MenuTitle title='ventings' />
                            </div>
                            <div className='links'>
                                <ul>
                                    {ventingMenuElem.map(
                                        (element: any, index: number) => {
                                            return (
                                                <li key={index}>
                                                    <MenuTitle
                                                        title={element.title}
                                                        link={element.link}
                                                        isLitteral={true}
                                                    />
                                                </li>
                                            );
                                        }
                                    )}
                                </ul>
                            </div>
                        </div>
                        <div className='footer-row'>
                            <div className='title'>
                                <MenuTitle title='accessories' />
                            </div>
                            <div className='links'>
                                <ul>
                                    {accessoriesMenuElem.map(
                                        (element: any, index: number) => {
                                            return (
                                                <li key={index}>
                                                    <MenuTitle
                                                        title={element.title}
                                                        link={
                                                            element.link + '/'
                                                        }
                                                        isLitteral={true}
                                                    />
                                                </li>
                                            );
                                        }
                                    )}
                                </ul>
                            </div>
                        </div>
                        <div className='footer-row support'>
                            <div className='title'>
                                <MenuTitle title='support-service' />
                            </div>
                            <div className='links'>
                                <ul>
                                    <li>
                                        <MenuTitle
                                            title='manuals'
                                            link='/archives/'
                                        />
                                    </li>
                                    <li>
                                        <MenuTitle
                                            title='warranty'
                                            link='/warranty/pellet-stoves/'
                                        />
                                    </li>
                                    <li>
                                        <MenuTitle
                                            title='where-to-buy'
                                            link='/where-to-buy/'
                                        />
                                    </li>
                                    <li>
                                        <MenuTitle
                                            title='contact'
                                            link='/contact/'
                                        />
                                    </li>
                                    <li>
                                        <MenuTitle
                                            title='videos'
                                            link='/videos/'
                                        />
                                    </li>
                                    <li>
                                        <MenuTitle
                                            title='blog'
                                            link='/blogArchives/'
                                        />
                                    </li>
                                    <li>
                                        <MenuTitle
                                            title='about'
                                            link='/about/'
                                        />
                                    </li>
                                    <li>
                                        <MenuTitle
                                            title='gov-incentives'
                                            link='/gov-incentives/'
                                        />
                                    </li>
                                    <li>
                                        <MenuTitle
                                            title='faq'
                                            link='/faq/wood-stoves/'
                                        />
                                    </li>
                                </ul>
                            </div>
                            <div className='title more-margin replacement-parts'>
                                <MenuTitle
                                    title='replacement-parts'
                                    link='/archives/'
                                />
                            </div>
                            <div className='title more-margin'>
                                <MenuTitle
                                    title='promotions'
                                    link='/promotions/'
                                />
                            </div>
                        </div>
                        <div className='footer-row titles'>
                            <div className='title more-margin'>
                                <MenuTitle
                                    title='order-status'
                                    link='/order-status/'
                                />
                            </div>
                            <div className='title more-margin'>
                                <a
                                    className='menu-title'
                                    onClick={handleMyAccountClick}
                                >
                                    {t(`menu-titles.my-account`)}
                                </a>
                            </div>
                            <div className='title more-margin'>
                                <MenuTitle
                                    title='test-reports'
                                    link='/test-reports/'
                                />
                            </div>
                            <div className='title more-margin'>
                                <MenuTitle
                                    title='return-policy'
                                    link='/return-policy/'
                                />
                            </div>
                            <div className='title more-margin'>
                                <MenuTitle
                                    title='shipping-policy'
                                    link='/shipping-policy/'
                                />
                            </div>
                            <div className='title more-margin'>
                                <MenuTitle
                                    title='privacy-policy'
                                    link='/privacy-policy/'
                                />
                            </div>
                            <div className='title more-margin'>
                                <MenuTitle
                                    title='terms-conditions'
                                    link='/terms-and-conditions/'
                                />
                            </div>
                        </div>
                        <div className='footer-right'>
                            <div className='contact-info-div'>
                                <div className='customer-services'>
                                    <div className='contact-info-title'>
                                        {
                                            data.customerServiceInfo.nodes[0]
                                                .title[currentLocale]
                                        }
                                    </div>
                                    <div className='contact-info-links'>
                                        <a
                                            className='contact-info'
                                            href={`mailto:${
                                                data.customerServiceInfo
                                                    .nodes[0].attributes.link
                                                    .values[0][currentLocale]
                                            }`}
                                        >
                                            {
                                                data.customerServiceInfo
                                                    .nodes[0].attributes.link
                                                    .values[0][currentLocale]
                                            }
                                        </a>
                                        <a
                                            className='contact-info'
                                            href={`tel:${
                                                data.customerServiceInfo
                                                    .nodes[0].attributes.texte_1
                                                    .values[0][currentLocale]
                                            }`}
                                        >
                                            {
                                                data.customerServiceInfo
                                                    .nodes[0].attributes.texte_1
                                                    .values[0][currentLocale]
                                            }
                                        </a>
                                    </div>
                                </div>
                                <div className='technical-support'>
                                    <div className='contact-info-title'>
                                        {
                                            data.technicalSupportInfo.nodes[0]
                                                .title[currentLocale]
                                        }
                                    </div>
                                    <div className='contact-info-links'>
                                        <a
                                            className='contact-info'
                                            href={`mailto:${
                                                data.technicalSupportInfo
                                                    .nodes[0].attributes.link
                                                    .values[0][currentLocale]
                                            }`}
                                        >
                                            {
                                                data.technicalSupportInfo
                                                    .nodes[0].attributes.link
                                                    .values[0][currentLocale]
                                            }
                                        </a>
                                        <a
                                            className='contact-info'
                                            href={`tel:${
                                                data.technicalSupportInfo
                                                    .nodes[0].attributes.texte_1
                                                    .values[0][currentLocale]
                                            }`}
                                        >
                                            {
                                                data.technicalSupportInfo
                                                    .nodes[0].attributes.texte_1
                                                    .values[0][currentLocale]
                                            }
                                        </a>
                                    </div>
                                </div>
                                <div className='retailer-services'>
                                    <div className='contact-info-title'>
                                        {
                                            data.retailerServiceInfo.nodes[0]
                                                .title[currentLocale]
                                        }
                                    </div>
                                    <div className='contact-info-links'>
                                        <a
                                            className='contact-info'
                                            href={`mailto:${
                                                data.retailerServiceInfo
                                                    .nodes[0].attributes.link
                                                    .values[0][currentLocale]
                                            }`}
                                        >
                                            {
                                                data.retailerServiceInfo
                                                    .nodes[0].attributes.link
                                                    .values[0][currentLocale]
                                            }
                                        </a>
                                        <a
                                            className='contact-info'
                                            href={`tel:${
                                                data.retailerServiceInfo
                                                    .nodes[0].attributes.texte_1
                                                    .values[0][currentLocale]
                                            }`}
                                        >
                                            {
                                                data.retailerServiceInfo
                                                    .nodes[0].attributes.texte_1
                                                    .values[0][currentLocale]
                                            }
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <SiteLocaleSelectionPopupMenu
                                    showCurrency={true}
                                />
                                <div className='socials'>
                                    <a
                                        href='https://www.facebook.com/EnglanderWoodStoves'
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        <img src={facebook} alt='' />
                                    </a>
                                    <a
                                        href='https://www.youtube.com/c/HeatRedefinedESW'
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        <img src={youtube} alt='' />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='footer-mobile'>
                        <img className='site-logo' src={EnglanderLogo} alt='' />
                        <div className='contact-info-div'>
                            <div className='customer-services'>
                                <div className='contact-info-title'>
                                    {
                                        data.customerServiceInfo.nodes[0].title[
                                            currentLocale
                                        ]
                                    }
                                </div>
                                <div className='contact-info-links'>
                                    <a
                                        className='contact-info'
                                        href={`mailto:${
                                            data.customerServiceInfo.nodes[0]
                                                .attributes.link.values[0][
                                                currentLocale
                                            ]
                                        }`}
                                    >
                                        {
                                            data.customerServiceInfo.nodes[0]
                                                .attributes.link.values[0][
                                                currentLocale
                                            ]
                                        }
                                    </a>
                                    <a
                                        className='contact-info'
                                        href={`tel:${
                                            data.customerServiceInfo.nodes[0]
                                                .attributes.texte_1.values[0][
                                                currentLocale
                                            ]
                                        }`}
                                    >
                                        {
                                            data.customerServiceInfo.nodes[0]
                                                .attributes.texte_1.values[0][
                                                currentLocale
                                            ]
                                        }
                                    </a>
                                </div>
                            </div>
                            <div className='technical-support'>
                                <div className='contact-info-title'>
                                    {
                                        data.technicalSupportInfo.nodes[0]
                                            .title[currentLocale]
                                    }
                                </div>
                                <div className='contact-info-links'>
                                    <a
                                        className='contact-info'
                                        href={`mailto:${
                                            data.technicalSupportInfo.nodes[0]
                                                .attributes.link.values[0][
                                                currentLocale
                                            ]
                                        }`}
                                    >
                                        {
                                            data.technicalSupportInfo.nodes[0]
                                                .attributes.link.values[0][
                                                currentLocale
                                            ]
                                        }
                                    </a>
                                    <a
                                        className='contact-info'
                                        href={`tel:${
                                            data.technicalSupportInfo.nodes[0]
                                                .attributes.texte_1.values[0][
                                                currentLocale
                                            ]
                                        }`}
                                    >
                                        {
                                            data.technicalSupportInfo.nodes[0]
                                                .attributes.texte_1.values[0][
                                                currentLocale
                                            ]
                                        }
                                    </a>
                                </div>
                            </div>
                            <div className='retailer-services'>
                                <div className='contact-info-title'>
                                    {
                                        data.retailerServiceInfo.nodes[0].title[
                                            currentLocale
                                        ]
                                    }
                                </div>
                                <div className='contact-info-links'>
                                    <a
                                        className='contact-info'
                                        href={`mailto:${
                                            data.retailerServiceInfo.nodes[0]
                                                .attributes.link.values[0][
                                                currentLocale
                                            ]
                                        }`}
                                    >
                                        {
                                            data.retailerServiceInfo.nodes[0]
                                                .attributes.link.values[0][
                                                currentLocale
                                            ]
                                        }
                                    </a>
                                    <a
                                        className='contact-info'
                                        href={`tel:${
                                            data.retailerServiceInfo.nodes[0]
                                                .attributes.texte_1.values[0][
                                                currentLocale
                                            ]
                                        }`}
                                    >
                                        {
                                            data.retailerServiceInfo.nodes[0]
                                                .attributes.texte_1.values[0][
                                                currentLocale
                                            ]
                                        }
                                    </a>
                                </div>
                            </div>
                        </div>
                        <hr />
                        <LocalizedLink to='/contact/'>
                            {t('menu-titles.contact')}
                        </LocalizedLink>
                        <LocalizedLink to='/about/'>
                            {t('menu-titles.about')}
                        </LocalizedLink>
                        <LocalizedLink to='/faq/wood-stoves/'>
                            {t('menu-titles.faq')}
                        </LocalizedLink>
                        <hr />
                        <LocalizedLink to='/order-status/'>
                            {t('menu-titles.order-status')}
                        </LocalizedLink>
                        <a onClick={handleMyAccountClick}>
                            {t(`menu-titles.my-account`)}
                        </a>
                        <LocalizedLink to='/return-policy/'>
                            {t('menu-titles.return-policy')}
                        </LocalizedLink>
                        <LocalizedLink to='/shipping-policy/'>
                            {t('menu-titles.shipping-policy')}
                        </LocalizedLink>
                        <LocalizedLink to='/privacy-policy/'>
                            {t('menu-titles.privacy-policy')}
                        </LocalizedLink>
                        <LocalizedLink to='/terms-and-conditions/'>
                            {t('menu-titles.terms-conditions')}
                        </LocalizedLink>
                        <LocalizedLink to='/test-reports/'>
                            {t('menu-titles.test-reports')}
                        </LocalizedLink>
                        <div className='bottom'>
                            <div className='socials'>
                                <a
                                    href='https://www.facebook.com/EnglanderWoodStoves'
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    <img src={facebook} alt='' />
                                </a>
                                <a
                                    href='https://www.youtube.com/c/HeatRedefinedESW'
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    <img src={youtube} alt='' />
                                </a>
                            </div>
                            <SiteLocaleSelectionPopupMenu showCurrency={true} />
                        </div>
                    </div>
                </div>
                <div className='developed-by'>
                    <a
                        target='__blank'
                        href='https://www.progymedia.com'
                        rel='noreferrer'
                    >
                        {t('layout:footer.developed-by')}{' '}
                        <img src={progiLogo} alt='Progymedia' />
                    </a>
                </div>
                <div className='copyright'>
                    {t('footer.copyright', {
                        year: new Date().getFullYear(),
                    })}
                </div>
            </div>
        );
    }

    function MenuTitle(props: {
        title: string;
        link?: string;
        isLitteral?: boolean;
    }) {
        return props.link ? (
            <LocalizedLink
                className='menu-title'
                to={props.link}
                label={
                    props.isLitteral
                        ? props.title
                        : t(`menu-titles.${props.title}`)
                }
            />
        ) : (
            <span className='menu-title'>
                {t(`menu-titles.${props.title}`)}
            </span>
        );
    }
}
